import { useFormik } from 'formik';
import Button from '../../components/button/Button';
import Checkbox from '../../components/checkBox/CheckBox';
import Card from '../../components/card/Card';
import DatePicker from '../../components/datepicker/DatePicker';
import DropDown from '../../components/dropdown/DropDown';
import Input from '../../components/input/Input';
import SearchableSelect from '../../components/sercheableselect/SearchableSelect';
import P from '../../components/texts/p/P';
import Span from '../../components/texts/span/Span';
import {
  basicData,
  clientData,
  corporateStructureData,
  applicationData,
} from '../../types/interfaces';
import { useEffect, useState, useMemo, useRef } from 'react';
import {
  getClientInfo,
  loadAssets,
  saveClientDocument,
  downloadClientDocument,
  saveClientInfo,
} from '../../services/ClientService';
import { provinces } from '../../mocks/Provinces';
import { uruguayDepartments } from '../../mocks/UruguayDepartments';
import { getUruguayLocalityItems } from '../../mocks/UruguayLocalities';
import { ivaStatuses } from '../../mocks/IvaStatuses';
import { legalEntities } from '../../mocks/LegalEntities';
import { normalizeBasicDataDatesForForm, normalizeToIsoDate } from '../../utils/dateUtils';
import Icon from '../../components/icon/Icon';
import Image from '../../components/image/Image';
import Spinner from '../../components/Spinner/spinner';
import RadioButton from '../../components/radiobutton/RadioButton';
import { economicActivities } from '../../mocks/EconomicActivities';
 import {
   getInitialClientData,
   initialClientData,
   MAX_FILE_SIZE,
 } from '../../utils/Constants';
import {
  isFinishedOrRejectedStatus,
} from '../../utils/applicationStatusUtils';
import useStatus from '../../hooks/useStatus';
import { NumericFormat } from 'react-number-format';
import { getApplicationsByCuit } from '../../services/ApplicationService';
import { useLocation, useParams } from 'react-router-dom';
import { useOfficers } from '../../hooks/useOfficers';
import { useReferrerNames } from '../../hooks/useReferrerNames';
import { ReferrerName } from '../../types/interfaces';
import { useReferrerPermissions } from '../../hooks/useReferrerPermissions';
import ReferrersManagementModal from '../../components/modals/ReferrersManagementModal';
import { useEconomicGroups } from '../../hooks/useEconomicGroups';
import EconomicGroupManagementModal from '../../components/modals/EconomicGroupManagementModal';
import { useToast } from '../../components/toast/toast';
import {
  LUFE_DEGRADED_TOAST_TITLE,
  shouldShowLufeDegradedToast,
} from '../../utils/lufeDegraded';
import { useCategoryTypes } from '../../hooks/useCategoryTypes';
import { useConfig } from '../../context/ConfigContext';
import { useManagements } from '../../hooks/useManagements';
import {
  applyManagementChangeToProducts,
  getManagementAssetRules,
  hasOriginationProductsLoaded,
  MANAGEMENT_CHANGE_WARNING,
  shouldConfirmManagementProductPurge,
} from '../../utils/managementAssetRules';
import {
  formatDocumentForSearch,
  getUruguayDocumentMaxLength,
  isValidUruguayDocumentLength,
  UruguayDocumentType,
} from './formatDocumentForSearch';
import {
  DGI_STATUS_OPTIONS,
  normalizeUruguayBasicData,
} from './normalizeUruguayClientData';
import Modal from '../../components/modal/modal';

interface BasicDataProps {
  basicData: clientData;
  onSave: Function;
  onSendStatus: Function;
  isSaving: boolean;
  applicationStatus: number | string;
  isValidatedClient: Function;
  currentStep?: number;
  previousStep?: number;
  onDataChange?: (clientInfo: clientData) => void;
  /** Current products (Paso 3); used to confirm purge when Gerencia changes. */
  products?: clientData['products'];
  onProductsUpdated?: (products: NonNullable<clientData['products']>) => void;
}

type activity = {
  code: number;
  label: string;
};

const UY_DATOS_GENERALES_GRID_3: React.CSSProperties = {
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
};

const UY_LOCALITY_POSTAL_GRID: React.CSSProperties = {
  gridTemplateColumns: 'minmax(0, 1.85fr) minmax(0, 1fr)',
};

const ASSET_DECLARATION_SAFE_ERROR_MSG =
  'No se pudo adjuntar la manifestación de bienes. Revisá los datos e intentá de nuevo.';

/** Maps unsafe API/storage upload messages to a user-safe Spanish string. */
export function toUserSafeUploadMessage(raw: string): string {
  if (!raw || typeof raw !== 'string') {
    return ASSET_DECLARATION_SAFE_ERROR_MSG;
  }
  const trimmed = raw.trim();
  if (
    trimmed.includes('stack_trace') ||
    trimmed.includes('ApiErrorResponse') ||
    /Not Found:\s*\{/i.test(trimmed) ||
    (trimmed.startsWith('{') && trimmed.includes('"'))
  ) {
    return ASSET_DECLARATION_SAFE_ERROR_MSG;
  }
  return trimmed;
}

const BasicData: React.FC<BasicDataProps> = ({
  basicData,
  onSave,
  onSendStatus,
  isSaving,
  applicationStatus,
  isValidatedClient,
  currentStep,
  previousStep,
  onDataChange,
  products,
  onProductsUpdated,
}) => {
  const { isApproved, isRejected, isInCommittee, isFinished } = useStatus({
    applicationStatus,
  });
  const { referrerNames, isLoading: isLoadingReferrerNames, refreshReferrerNames } = useReferrerNames();
  const { officers, isLoading: isLoadingOfficers } = useOfficers();
  const { hasPermission } = useReferrerPermissions();
  const { categoryTypes, isLoading: isLoadingCategoryTypes } = useCategoryTypes();
  const { economicGroups, isLoading: isLoadingEconomicGroups, refreshEconomicGroups } = useEconomicGroups();
  const [isReferrersModalOpen, setIsReferrersModalOpen] = useState(false);
  const [isEconomicGroupModalOpen, setIsEconomicGroupModalOpen] = useState(false);
  const [pendingManagementId, setPendingManagementId] = useState<number | null>(null);
  const [isApplyingManagementChange, setIsApplyingManagementChange] = useState(false);
  
  const config = useConfig();
  const isClientCategoryTypeEnabled = config.isClientCategoryTypeEnabled ?? false;
  const isEconomicGroupEnabled = config.isEconomicGroupEnabled ?? false;
  const isManagementSelectionEnabled = config.isManagementSelectionEnabled ?? false;
  const isUruguayEnabled = config.isUruguayEnabled ?? false;
  const isUruguayCountry = config.country === 'URUGUAY';
  const { managements, isLoading: isLoadingManagements } = useManagements(isManagementSelectionEnabled);
  const prepareUruguayBasicData = (data: basicData) =>
    isUruguayEnabled ? normalizeUruguayBasicData(data) : data;
  const [documentSearchType, setDocumentSearchType] =
    useState<UruguayDocumentType>('RUT');

  const managementDropdownItems = useMemo(
    () => managements.map((m) => ({ label: m.label, disabled: false, code: String(m.id) })),
    [managements],
  );
  
  const [errorMsg, setErrorMsg] = useState('');
  const [isClientFound, setIsClientFound] = useState<boolean>(false);
  const [isApplicationOnCourse, setIsApplicationOnCourse] =
    useState<boolean>(false);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [hasPendingApplication, setHasPendingApplication] = useState<boolean>(false);
  const [isCuitValid, setIsCuitValid] = useState<boolean>(true);
  const [validatedBasicData, setValidatedBasicData] = useState<boolean>(true);
  const [uploadingAssetIndex, setUploadingAssetIndex] = useState<number | null>(null);
  const pendingAssetUploadsRef = useRef(0);
  const [pendingAssetUploadsCount, setPendingAssetUploadsCount] = useState(0);
  const [assetUploadError, setAssetUploadError] = useState<{ [key: number]: string }>({});
  const [hasClientBeenSaved, setHasClientBeenSaved] = useState<boolean>(false);
  const [searchedCuit, setSearchedCuit] = useState<string>('');
  const [clientInfo, setClientInfo] = useState<clientData>(
    basicData || getInitialClientData()
  );

  // Remount of Paso 1 resets local state to RUT; re-infer from loaded document digits.
  useEffect(() => {
    if (!isUruguayEnabled) return;
    const digits = String(basicData?.cuit ?? clientInfo.cuit ?? '').replace(/\D/g, '');
    if (digits.length === 7 || digits.length === 8) {
      setDocumentSearchType('Cedula');
    } else if (digits.length === 12) {
      setDocumentSearchType('RUT');
    }
  }, [isUruguayEnabled, basicData?.cuit, clientInfo.cuit]);

  const location = useLocation();
  const { id: urlApplicationId } = useParams<{ id?: string }>();
  
  // Ref para rastrear si el componente cargó una solicitud existente (desde dashboard)
  // Se inicializa en false y se actualiza en useEffect para manejar carga asíncrona
  const isExistingApplicationRef = useRef<boolean>(false);

  // Detectar si es una solicitud existente (abierta desde dashboard)
  // Solo se considera existente si la URL tiene el patrón /applications/:id
  // NO se considera existente si el usuario busca un CUIT en /clientes
  useEffect(() => {
    const isExistingApplicationUrl = location.pathname.includes('/applications/');
    if (!isExistingApplicationRef.current && isExistingApplicationUrl && basicData?.id && basicData.id !== '0' && basicData.id !== '') {
      isExistingApplicationRef.current = true;
    }
  }, [basicData?.id, location.pathname]);

  // Helper para convertir ReferrerName a string (code) para enviar al backend
  const getReferrerNameCode = (referrerName: string | ReferrerName | undefined): string => {
    if (!referrerName) return '';
    if (typeof referrerName === 'string') return referrerName;
    return referrerName.code;
  };

  // Helper para convertir string a ReferrerName cuando se recibe del backend
  const getReferrerNameFromCode = (code: string | undefined): ReferrerName | string => {
    if (!code) return '';
    const referrerName = referrerNames.find(rn => rn.code === code);
    return referrerName || code; // Si no se encuentra, devolver el código como string (compatibilidad)
  };

  // Helper para obtener el label del referrer name
  const getReferrerNameLabel = (referrerName: string | ReferrerName | undefined): string => {
    if (!referrerName) return '';
    if (typeof referrerName === 'string') {
      const found = referrerNames.find(rn => rn.code === referrerName);
      return found ? found.name : referrerName;
    }
    return referrerName.name;
  };

  // Convertir referrerNames a items para DropDown (memoizado porque se usa en JSX)
  const referrerNameItems = useMemo(() => {
    return referrerNames.map((referrerName) => ({
      label: referrerName.name,
      disabled: false,
      code: referrerName.code,
    }));
  }, [referrerNames]);

  const economicGroupItems = useMemo(() => {
    return economicGroups.map((group) => ({
      label: group.name,
      disabled: false,
      code: group.code,
    }));
  }, [economicGroups]);

  // Preparar items para el dropdown de category types (sin useMemo, directo como en CheckSection)
  const categoryTypeItems = (Array.isArray(categoryTypes) ? categoryTypes : [])?.map(ct => ({
    label: ct.name,
    disabled: false
  })) || [];

  const regionOptions = isUruguayEnabled ? uruguayDepartments : provinces;
  const regionLabel = isUruguayEnabled ? 'Departamento' : 'Provincia';
  const personeriaItems = isUruguayEnabled
    ? legalEntities.filter((le) => le.label === 'Física' || le.label === 'Jurídica')
    : legalEntities;

  const renderYesNoRadioGroup = (
    label: string,
    name: string,
    value: boolean | null | undefined,
    onSelect: (selected: boolean) => void,
    error?: string,
  ) => (
    <Card width={isUruguayEnabled ? '100%' : '30vw'}>
      <P withMargin={false} variant='neutral-1' type='4'>
        {label}
      </P>
      <Card display='flex' gap='24px' margin={{ top: '3' }}>
        <RadioButton
          id={`${name}-yes`}
          name={name}
          label='Sí'
          checked={value === true}
          disabled={!isApplicationEditable()}
          onChange={() => onSelect(true)}
        />
        <RadioButton
          id={`${name}-no`}
          name={name}
          label='No'
          checked={value === false}
          disabled={!isApplicationEditable()}
          onChange={() => onSelect(false)}
        />
      </Card>
      <Span variant='danger' type='2'>
        {error}
      </Span>
    </Card>
  );

  // Helper para obtener el título del category type seleccionado
  const getSelectedCategoryType = () => {
    const categoryType = categoryTypes?.find(ct => ct.id === clientInfo.category_type_id);
    if (!categoryType) return isUruguayEnabled ? 'Elegir rubro' : 'Seleccionar rubro';
    return categoryType.name;
  };

  const handleCategoryTypeChange = (selectedLabel: string) => {
    const categoryType = categoryTypes?.find(ct => ct.name === selectedLabel);
    if (categoryType) {
      setClientInfo((prev) => ({
        ...prev,
        category_type_id: categoryType.id,
      }));
    }
  };

  // Revalidar el formulario cuando cambie category_type_id para que el botón Continuar se active correctamente
  useEffect(() => {
    if (!isClientCategoryTypeEnabled) return;
    basicDataFormik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientInfo.category_type_id, isClientCategoryTypeEnabled]);

  const resetAndValidateForms = (
    basicData: any,
    corporateStructure: any,
    officer: any,
    managementId: number | string = '',
  ) => {
    basicDataFormik.resetForm({
      values: {
        ...normalizeBasicDataDatesForForm(basicData),
        officer: officer,
        management_id: managementId,
      },
    });
    societaryFormik.resetForm({ values: corporateStructure });

    setTimeout(() => {
      basicDataFormik.setTouched({
        ...Object.keys(basicData).reduce(
          (acc, key) => {
            acc[key] = true;
            return acc;
          },
          {} as Record<string, boolean>
        ),
        officer: true,
        management_id: true,
      });
      societaryFormik.setTouched(
        corporateStructure.map(() => ({
          ci: true,
          first_name: true,
          ownership_interest: true,
          position: true,
          assets: true,
          value: true,
        }))
      );
      basicDataFormik.validateForm();
      societaryFormik.validateForm();
    }, 0);
  };

  useEffect(() => {
    if (basicData.cuit) {
      const hasPendingApp = basicData.id_application &&
                           basicData.id_application !== null &&
                           basicData.id_application !== undefined &&
                           basicData.id_application !== 0;

      // Si estamos editando una solicitud existente (hay urlApplicationId), no ocultar los datos
      const isEditingExistingApplication = urlApplicationId && urlApplicationId !== '0' && urlApplicationId !== '';

      // Si estamos editando una solicitud existente, siempre mostrar los datos
      if (isEditingExistingApplication && basicData.basic_data && basicData.basic_data.company_name) {
        setHasPendingApplication(false);
        setIsClientFound(true);
        setIsApplicationOnCourse(true);
        setHasClientBeenSaved(true);
        isValidatedClient(true);
        setSearchedCuit(formatDocumentForSearch(basicData.cuit, isUruguayEnabled));
        isExistingApplicationRef.current = true;
        
        // Actualizar los formularios si no están actualizados
        if (!clientInfo.basic_data?.company_name || clientInfo.basic_data.company_name !== basicData.basic_data.company_name) {
          setClientInfo(basicData);
          basicDataFormik.setValues({
            ...normalizeBasicDataDatesForForm(
              prepareUruguayBasicData(basicData.basic_data),
            ),
            officer: basicData.officer,
            management_id: basicData.management_id ?? '',
          });
          societaryFormik.setValues(basicData.corporate_structure || []);
          cuitFormik.setFieldValue(
            'cuit',
            formatDocumentForSearch(basicData.cuit, isUruguayEnabled)
          );
        }
        
        basicDataFormik.validateForm();
        societaryFormik.validateForm();
        return;
      }

      // Si hay solicitud pendiente y NO estamos editando una existente, ocultar datos y limpiar formularios
      if (hasPendingApp) {
        setHasPendingApplication(true);
        setIsClientFound(false);
        setIsApplicationOnCourse(false);
        setHasClientBeenSaved(false); // Resetear para permitir nueva búsqueda
        isExistingApplicationRef.current = false; // Resetear ref
        // Limpiar los datos del formulario para que no se muestren cuando hay solicitud pendiente
        setClientInfo(getInitialClientData());
        basicDataFormik.resetForm({ values: { ...initialClientData.basic_data, officer: '', management_id: '' } });
        societaryFormik.resetForm({ values: [] });
        cuitFormik.setFieldValue(
          'cuit',
          formatDocumentForSearch(basicData.cuit, isUruguayEnabled)
        );
        return;
      }

      // Si no hay solicitud pendiente, mostrar los datos
      if (!hasPendingApp) {
        setHasPendingApplication(false);
        setIsClientFound(true);
        setIsApplicationOnCourse(true);
      }
      isValidatedClient(true);
      setSearchedCuit(basicData.cuit);
      basicDataFormik.validateForm();
      societaryFormik.validateForm();
      if (isExistingApplicationRef.current && basicData.id && basicData.id !== '0' && basicData.id !== '') {
        setHasClientBeenSaved(true);
      }
    }
  }, [basicData, urlApplicationId]);

  // Sincronizar el estado local con las props del padre
  useEffect(() => {
    if (basicData && basicData.cuit && basicData.basic_data) {
      // Solo actualizar si estamos editando una solicitud existente (hay urlApplicationId)
      // NO actualizar si estamos creando una nueva carpeta (no hay urlApplicationId) y hay solicitud pendiente
      const isEditingExistingApplication = currentStep === 1 && 
        urlApplicationId && 
        urlApplicationId !== '0' && 
        urlApplicationId !== '';
      
      // Si NO estamos editando una solicitud existente, no actualizar los datos
      // Esto previene que se muestren datos cuando hay una solicitud pendiente y se intenta crear una nueva carpeta
      if (!isEditingExistingApplication) {
        return;
      }
      
      // Solo actualizar si los datos del padre tienen más información que el estado local
      // O si estamos editando una solicitud existente y los datos no están cargados
      const basicDataReferrerCode = getReferrerNameCode(basicData.referrer_name);
      const clientInfoReferrerCode = getReferrerNameCode(clientInfo.referrer_name);
      const hasMoreData = 
        (basicData.corporate_structure?.length || 0) > (clientInfo.corporate_structure?.length || 0) ||
        (basicData.officer && !clientInfo.officer) ||
        (basicData.referrer_name && !clientInfo.referrer_name) ||
        (basicDataReferrerCode && !clientInfoReferrerCode);
      
      const needsUpdate = hasMoreData || 
        (isEditingExistingApplication && (!clientInfo.basic_data?.company_name || !isClientFound));
      
      if (needsUpdate) {
        setClientInfo(basicData);
        basicDataFormik.setValues({
          ...normalizeBasicDataDatesForForm(
            prepareUruguayBasicData(basicData.basic_data),
          ),
          officer: basicData.officer,
          management_id: basicData.management_id ?? '',
        });
        societaryFormik.setValues(basicData.corporate_structure || []);
        
        // Si estamos editando una solicitud existente, asegurar que los estados estén correctos
        if (isEditingExistingApplication) {
          setIsClientFound(true);
          setIsApplicationOnCourse(true);
          setHasPendingApplication(false);
          setSearchedCuit(formatDocumentForSearch(basicData.cuit, isUruguayEnabled));
          cuitFormik.setFieldValue(
            'cuit',
            formatDocumentForSearch(basicData.cuit, isUruguayEnabled)
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicData?.cuit, basicData?.corporate_structure?.length, basicData?.officer, basicData?.referrer_name, basicData?.basic_data?.company_name, currentStep, urlApplicationId]);

  // Cargar datos automáticamente cuando se navega al paso 1 desde otro paso con una solicitud en curso
  useEffect(() => {
    // Si estamos en el paso 1, hay una solicitud en curso (id en URL), y tenemos datos del cliente
    const isNavigatingToStep1 = currentStep === 1;
    const hasApplicationInUrl = urlApplicationId && urlApplicationId !== '0' && urlApplicationId !== '';
    const hasClientData = basicData?.cuit && 
      basicData?.id && 
      basicData.id !== '0' && 
      basicData.id !== '' &&
      basicData.basic_data &&
      basicData.basic_data.company_name; // Asegurar que hay datos reales
    
    // Solo cargar si venimos de otro paso o si los datos no están mostrados
    const shouldLoadData = isNavigatingToStep1 && 
      hasApplicationInUrl && 
      hasClientData &&
      (!isClientFound || !clientInfo.basic_data?.company_name);
    
    if (shouldLoadData) {
      // Verificar si es una solicitud en curso (no finalizada) o si no tenemos el estado aún
      const isApplicationInProgress = !applicationStatus ||
        !isFinishedOrRejectedStatus(applicationStatus);
      
      if (isApplicationInProgress) {
        // Cargar los datos automáticamente
        setClientInfo(basicData);
        setSearchedCuit(formatDocumentForSearch(basicData.cuit, isUruguayEnabled));
        setIsClientFound(true);
        setIsApplicationOnCourse(true);
        setHasPendingApplication(false);
        setHasClientBeenSaved(true);
        isValidatedClient(true);
        isExistingApplicationRef.current = true;
        
        basicDataFormik.setValues({
          ...normalizeBasicDataDatesForForm(
            prepareUruguayBasicData(basicData.basic_data),
          ),
          officer: basicData.officer,
          management_id: basicData.management_id ?? '',
        });
        societaryFormik.setValues(basicData.corporate_structure || []);
        
        cuitFormik.setFieldValue(
          'cuit',
          formatDocumentForSearch(basicData.cuit, isUruguayEnabled)
        );
        
        // Validar los formularios
        setTimeout(() => {
          basicDataFormik.validateForm();
          societaryFormik.validateForm();
        }, 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, urlApplicationId, basicData?.cuit, basicData?.id, basicData?.basic_data?.company_name, applicationStatus]);

  // Detectar navegación entre pasos y mantener el estado
  useEffect(() => {
    if (currentStep === 1 && clientInfo.cuit) {
      // Logs de debugging removidos - el fix está funcionando
      
      // Solo sincronizar si el estado local tiene más datos que el padre
      const clientInfoReferrerCode = getReferrerNameCode(clientInfo.referrer_name);
      const basicDataReferrerCode = getReferrerNameCode(basicData?.referrer_name);
      const hasLocalChanges = 
        societaryFormik.values.length > (basicData?.corporate_structure?.length || 0) ||
        (basicDataFormik.values.officer && !basicData?.officer) ||
        (clientInfoReferrerCode && !basicDataReferrerCode);
      
      if (hasLocalChanges) {
        const { officer, management_id: rawMid, ...basicDataFields } =
          basicDataFormik.values as basicData & {
            officer?: string;
            management_id?: number | string;
          };
        const updatedClientInfo = {
          ...clientInfo,
          basic_data: basicDataFields,
          corporate_structure: societaryFormik.values,
          officer: officer ?? '',
          referrer_name: clientInfo.referrer_name,
          management_id:
            rawMid === '' || rawMid === undefined || rawMid === null ? undefined : Number(rawMid),
        };
        if (onDataChange) {
          onDataChange(updatedClientInfo);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  useEffect(() => {
    if (
      location.pathname === '/clientes' &&
      currentStep === 1 &&
      previousStep === 1
    ) {
      setClientInfo(getInitialClientData());
      setHasClientBeenSaved(false); // Resetear cuando se resetea el cliente
      isExistingApplicationRef.current = false; // Resetear ref para permitir nueva búsqueda
      setSearchedCuit(''); // Resetear el CUIT buscado
      setIsClientFound(false);
      setIsApplicationOnCourse(false);
      setIsSearching(false);
      setHasPendingApplication(false);
      setIsCuitValid(true);
      setValidatedBasicData(true);
      setErrorMsg('');
      resetAndValidateForms(
        { ...initialClientData.basic_data, date_incorporation: '' },
        initialClientData.corporate_structure,
        initialClientData.officer,
        '',
      );
      cuitFormik.resetForm({ values: { cuit: '' } });
    }
  }, [location.pathname, currentStep, previousStep]);

  const buildBasicDataForApi = <T extends object>(basicData: T): T =>
    normalizeBasicDataDatesForForm(basicData) as T;

  const loadClientData = (response: any, cuitValue: string, cuitFormatted: string) => {
    const apiBasic = response.data.basic_data || {};
    const basicDataWithLegalAddress = {
      ...apiBasic,
      legal_company_address: apiBasic.legal_company_address || apiBasic.company_address || '',
      legal_city: apiBasic.legal_city || apiBasic.city || '',
      legal_state: apiBasic.legal_state || apiBasic.state || '',
    };
    const normalizedBasicData = isUruguayEnabled
      ? normalizeUruguayBasicData(basicDataWithLegalAddress)
      : basicDataWithLegalAddress;
    const updatedResponseData = {
      ...response.data,
      basic_data: normalizedBasicData,
      category_type_id: response.data.category_type?.id || response.data.category_type_id,
      verified: true,
    };
    setClientInfo(updatedResponseData);
    setSearchedCuit(cuitFormatted);

    if (onDataChange) {
      onDataChange(updatedResponseData);
    }
    setIsCuitValid(true);
    isValidatedClient(true);
    setErrorMsg('');

    if (shouldShowLufeDegradedToast(response)) {
      useToast.info({
        title: LUFE_DEGRADED_TOAST_TITLE,
        id: 'lufe-degraded-toast',
        description: '',
        closeButton: true,
        autoClose: 5000,
      });
    }

    resetAndValidateForms(
      normalizedBasicData,
      response.data.corporate_structure || [],
      response.data.officer || '',
      response.data.management_id ?? '',
    );
    setValidatedBasicData(true);
    setIsClientFound(true);
    setIsSearching(false);
  };

  const cuitFormik = useFormik({
    initialValues: { cuit: clientInfo.cuit },
    onSubmit: (values) => {
      if (!values.cuit || values.cuit.length === 0) {
        setErrorMsg(
          isUruguayEnabled
            ? documentSearchType === 'RUT'
              ? 'Debes ingresar un RUT'
              : 'Debes ingresar una Cédula'
            : 'Debes ingresar un CUIT'
        );
        return;
      }
      const cuitWithoutDashes = values.cuit.replace(/-/g, '');
      if (isUruguayEnabled) {
        if (!isValidUruguayDocumentLength(cuitWithoutDashes, documentSearchType)) {
          setErrorMsg(
            documentSearchType === 'RUT'
              ? 'El RUT ingresado tiene un formato incorrecto'
              : 'La Cédula ingresada tiene un formato incorrecto'
          );
          return;
        }
      } else if (cuitWithoutDashes.length !== 11) {
        setErrorMsg('El CUIT ingresado tiene un formato incorrecto');
        return;
      }
      {
        setErrorMsg('');
        setIsSearching(true);
        getClientInfo(cuitWithoutDashes).then((response) => {
          if (response.success && 'data' in response) {
            const cuitValue = cuitWithoutDashes;

            const applicationId = response.data?.id_application;
            const responseId = response.data?.id;
            const valueToSend = applicationId || responseId;

            const hasValidApplicationId = valueToSend != null &&
                                         valueToSend !== undefined &&
                                         valueToSend !== '' &&
                                         String(valueToSend).trim() !== '' &&
                                         String(valueToSend).trim() !== '-1' &&
                                         valueToSend !== 0;

            if (hasValidApplicationId) {
              getApplicationsByCuit(cuitValue).then((appsResponse) => {
                if (appsResponse.success && 'data' in appsResponse) {
                  const applications = appsResponse.data?.content || [];

                  let hasActiveApplication = false;
                  let isEditingCurrentApplication = false;

                  // Verificar si estamos editando la solicitud actual
                  if (urlApplicationId) {
                    const currentAppId = String(urlApplicationId);
                    isEditingCurrentApplication = applications.some((app: any) => 
                      String(app.id) === currentAppId
                    );
                  }

                  applications.forEach((app: any) => {
                    if (app.status?.id || app.status?.code) {
                      const isFinalStatus = isFinishedOrRejectedStatus(app.status);

                      if (!isFinalStatus) {
                        hasActiveApplication = true;
                      }
                    } else {
                      hasActiveApplication = true;
                    }
                  });

                  // Si estamos editando la solicitud actual, permitir cargar los datos
                  // Solo bloquear si estamos creando una nueva solicitud y hay una activa
                  if (hasActiveApplication && !isEditingCurrentApplication) {
                    setHasPendingApplication(true);
                    setIsClientFound(false);
                    setIsApplicationOnCourse(false);

                    onSendStatus(valueToSend);

                    setClientInfo({
                      ...getInitialClientData(),
                      cuit: cuitValue
                    });

                    basicDataFormik.resetForm({ values: { ...initialClientData.basic_data, officer: '', management_id: '' } });
                    societaryFormik.resetForm({ values: [] });

                    setIsSearching(false);
                    setErrorMsg('');

                    return;
                  } else {
                    setHasPendingApplication(false);
                    // Si estamos editando la solicitud actual, cargar datos sin mensaje
                    // Si no hay solicitudes activas, mostrar mensaje informativo
                    if (!isEditingCurrentApplication) {
                      useToast.success({
                        title: 'Ya existe el cliente. No tiene solicitudes vigentes.',
                        id: 'save-toast',
                        description: '',
                        closeButton: true,
                        autoClose: 5000,
                      });
                    }
                    loadClientData(response, cuitValue, values.cuit);
                  }
                } else {
                  setHasPendingApplication(true);
                  setIsClientFound(false);
                  setIsApplicationOnCourse(false);
                  onSendStatus(valueToSend);
                  setClientInfo({
                    ...getInitialClientData(),
                    cuit: cuitValue
                  });
                  basicDataFormik.resetForm({ values: { ...initialClientData.basic_data, officer: '', management_id: '' } });
                  societaryFormik.resetForm({ values: [] });
                  setIsSearching(false);
                  setErrorMsg('');
                }
              }).catch(() => {
                setHasPendingApplication(true);
                setIsClientFound(false);
                setIsApplicationOnCourse(false);
                setIsSearching(false);
                setErrorMsg('Error al verificar el estado de la solicitud');
              });
            } else {
              setHasPendingApplication(false);
              useToast.info({
                title: 'Ya existe el cliente. No tiene solicitudes vigentes.',
                id: 'client-exists-toast',
                description: '',
                closeButton: true,
                autoClose: 5000,
              });
              loadClientData(response, cuitValue, values.cuit);
            }
          } else {
            setIsCuitValid(false);
            isValidatedClient(false);
            setValidatedBasicData(false);

            // Crear un nuevo clientData con basic_data vacío pero con el nuevo CUIT
            const newClientData = {
              ...getInitialClientData(),
              cuit: values.cuit.replace(/-/g, ''),
              verified: false,
            };
            
            // Resetear el formulario con datos vacíos
            resetAndValidateForms(
              initialClientData.basic_data,
              [],
              ''
            );
            
            setClientInfo(newClientData);
            setSearchedCuit(values.cuit);
            
            // Actualizar el CUIT en el padre con los datos limpios
            if (onDataChange) {
              onDataChange(newClientData);
            }
            
            setIsClientFound(true);
          }
          setIsSearching(false);
        });
      }
    },
  });

  const validateBasicData = (values: basicData) => {
    const errors: any = {};
    const req = (message: string) => (isUruguayEnabled ? 'Campo obligatorio' : message);
    if (!values.company_name) {
      errors.company_name = req('El campo Razón social es obligatorio');
    }
    if (!values.company_address) {
      errors.company_address = req('El campo Domicilio fiscal es obligatorio');
    }
    if (!values.state) {
      errors.state = req(
        isUruguayEnabled
          ? 'El campo Departamento es obligatorio'
          : 'El campo Provincia es obligatorio',
      );
    }
    if (!values.city) {
      errors.city = req('El campo Localidad es obligatorio');
    }
    if (!values.email) {
      errors.email = req('El campo email es obligatorio');
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)
    ) {
      errors.email = 'Formato de email inválido';
    }
    if (!values.legal_entity) {
      errors.legal_entity = req('El campo Personería es obligatorio');
    }
    if (!isUruguayEnabled && !values.activity) {
      errors.activity = 'El campo Actividad es obligatorio';
    }
    if (!isUruguayEnabled && !values.iva_status) {
      errors.iva_status = 'El campo Condición frente al IVA es obligatorio';
    }
    if (isClientCategoryTypeEnabled && !clientInfo.category_type_id) {
      errors.category_type_id = req('El campo Rubro es obligatorio');
    }
    if (!isUruguayCountry && !values.date_incorporation) {
      errors.date_incorporation = req('El campo Fecha de constitución es obligatorio');
    }
    if (isManagementSelectionEnabled) {
      const mid = (values as basicData & { management_id?: number | string }).management_id;
      if (mid === '' || mid === undefined || mid === null) {
        errors.management_id = req('El campo Gerencia es obligatorio');
      }
    }
    if (isUruguayEnabled) {
      if (values.contractual_link === null || values.contractual_link === undefined) {
        errors.contractual_link = req('El campo Vínculo contractual es obligatorio');
      }
      if (values.politically_exposed === null || values.politically_exposed === undefined) {
        errors.politically_exposed = req('El campo PEP es obligatorio');
      }
      if (values.state_supplier_registry === null || values.state_supplier_registry === undefined) {
        errors.state_supplier_registry = req('El campo RUPE es obligatorio');
      }
      if (!values.dgi_status) {
        errors.dgi_status = req('El campo Estado DGI es obligatorio');
      }
      if (!values.bps_certificate_number) {
        errors.bps_certificate_number = req('El campo Nº certificado BPS es obligatorio');
      }
    }
    return errors;
  };

  const basicDataFormik = useFormik({
    initialValues: {
      ...normalizeBasicDataDatesForForm(clientInfo.basic_data || {}),
      officer: clientInfo.officer,
      management_id: clientInfo.management_id ?? '',
    },
    validate: validateBasicData,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: (values) => {},
  });

  const fiscalLocalityItems = useMemo(
    () => getUruguayLocalityItems(basicDataFormik.values.state),
    [basicDataFormik.values.state],
  );
  const legalLocalityItems = useMemo(
    () => getUruguayLocalityItems(basicDataFormik.values.legal_state),
    [basicDataFormik.values.legal_state],
  );

  const dgiStatusItems = useMemo(() => {
    const base = [...DGI_STATUS_OPTIONS];
    const current = basicDataFormik.values.dgi_status?.trim();
    const labels = current && !base.includes(current as typeof base[number])
      ? [current, ...base]
      : base;
    return labels.map((label) => ({ label }));
  }, [basicDataFormik.values.dgi_status]);

  useEffect(() => {
    if (!isManagementSelectionEnabled) return;
    void basicDataFormik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManagementSelectionEnabled]);

  const getActivityLabel = useMemo(() => {
    const activityValue = basicDataFormik.values.activity;
    
    if (!activityValue) return '';
    
    if (typeof activityValue === 'string') {
      return activityValue.trim();
    }
    
    if (typeof activityValue === 'number') {
      const foundActivity = economicActivities.find(
        (item) => item.code === activityValue
      );
      return foundActivity?.label || String(activityValue);
    }
    
    return String(activityValue);
  }, [basicDataFormik.values.activity]);

  useEffect(() => {
    for (const field of ['date_incorporation', 'dgi_issue_date', 'dgi_expiration_date', 'bps_issue_date', 'bps_expiration_date'] as const) {
      const currentDate = basicDataFormik.values[field];
      if (currentDate && typeof currentDate === 'string' && currentDate.includes('undefined')) {
        basicDataFormik.setFieldValue(field, normalizeToIsoDate(currentDate));
      }
    }
  }, [
    basicDataFormik.values.date_incorporation,
    basicDataFormik.values.dgi_issue_date,
    basicDataFormik.values.dgi_expiration_date,
    basicDataFormik.values.bps_issue_date,
    basicDataFormik.values.bps_expiration_date,
  ]);

  const applyManagementId = async (mId: number | undefined) => {
    basicDataFormik.setFieldValue('management_id', mId ?? '');
    const { officer: curOfficer, management_id: _mid, ...curBasicFields } =
      basicDataFormik.values as basicData & {
        officer?: string;
        management_id?: number | string;
      };
    const code = managements.find((m) => m.id === mId)?.code;
    const rules = getManagementAssetRules(code);
    const currentProducts = (products ?? clientInfo.products ?? []) as applicationData[];

    let nextProducts = currentProducts;
    if (rules) {
      let assets: Array<{ code: string; saleValue?: unknown }> = [];
      if ((config.isQuotationsEnabled ?? false) && currentProducts.length > 0) {
        try {
          const res = await fetch('/v1/private/applications/quotations', {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          });
          const data = await res.json();
          assets = Array.isArray(data) ? data : data?.data || [];
        } catch {
          // Keep filtered products with previous amounts if quotations cannot be loaded.
        }
      }
      nextProducts = applyManagementChangeToProducts(currentProducts, code, assets);
    }

    const nextClient: clientData = {
      ...clientInfo,
      management_id: mId,
      basic_data: { ...clientInfo.basic_data, ...curBasicFields },
      officer: curOfficer ?? clientInfo.officer,
      ...(rules ? { products: nextProducts } : {}),
    };
    setClientInfo(nextClient);
    if (rules) {
      onProductsUpdated?.(nextProducts);
    }
    onDataChange?.(nextClient);
  };

  const handleManagementSelect = (selectedLabel: string) => {
    const m = managements.find((item) => item.label === selectedLabel);
    const mId = m?.id;
    if (mId == null) return;

    const currentRaw = (basicDataFormik.values as basicData & { management_id?: number | string })
      .management_id;
    if (
      currentRaw !== '' &&
      currentRaw != null &&
      Number(currentRaw) === mId
    ) {
      return;
    }

    const currentProducts = products ?? clientInfo.products ?? [];
    if (
      shouldConfirmManagementProductPurge(
        m.code,
        hasOriginationProductsLoaded(currentProducts)
      )
    ) {
      setPendingManagementId(mId);
      return;
    }
    void applyManagementId(mId);
  };

  const validateCorporateStructure = (values: corporateStructureData[]) => {
    const errors: any[] = [];
    let totalOwnershipInterest = 0;
    const documentLabel = isUruguayEnabled ? 'Cédula' : 'Documento';

    values.forEach((partner) => {
      totalOwnershipInterest += Number(partner.ownership_interest);
    });

    values.forEach((partner, index) => {
      const partnerErrors: any = {};
      if (!partner.ci) {
        partnerErrors.ci = `El campo ${documentLabel} es obligatorio`;
      } else {
        const digits = String(partner.ci).replace(/\D/g, '');
        if (isUruguayEnabled) {
          if (!isValidUruguayDocumentLength(digits, 'Cedula')) {
            partnerErrors.ci =
              'La cédula del socio debe tener 7 u 8 dígitos';
          }
        } else if (digits.length !== 11) {
          partnerErrors.ci = 'El documento del socio debe tener 11 dígitos';
        }
      }
      if (!partner.first_name) {
        partnerErrors.first_name = 'El campo Nombre es obligatorio';
      }
      if (totalOwnershipInterest > 100) {
        partnerErrors.ownership_interest =
          'La suma de las participaciones no debe superar el 100%';
      }
      if (Object.keys(partnerErrors).length > 0) {
        errors[index] = partnerErrors;
      }
    });
    return errors;
  };

  const societaryFormik = useFormik({
    initialValues: clientInfo.corporate_structure || [
      {
        id: 0,
        person_id: 0,
        ci: '',
        first_name: '',
        last_name: '',
        ownership_interest: '0.00',
        position: '',
        assets: '',
        assets_file_path: undefined,
        value: 0.0,
      },
    ],
    validate: validateCorporateStructure,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: (values) => {},
  });

  const addCorporateStructure = () => {
    const newPartner = {
      id: 0,
      person_id: 0,
      ci: '',
      first_name: '',
      last_name: '',
      ownership_interest: '0.00',
      position: '',
      assets: '',
      assets_file_path: undefined,
      value: 0.0,
    };

    const updatedValues = [...societaryFormik.values, newPartner];
    societaryFormik.setValues(updatedValues);

    const updatedClientInfo = {
      ...clientInfo,
      corporate_structure: updatedValues,
    };
    
    setClientInfo(updatedClientInfo);
    // Logs de debugging removidos - el fix está funcionando
    // Notificar al padre sobre los cambios
    if (onDataChange) {
      onDataChange(updatedClientInfo);
    }
  };

  const removeCorporateMember = (index: number) => {
    const updatedValues = societaryFormik.values.filter((_, i) => i !== index);

    societaryFormik.setValues(updatedValues);

    const updatedClientInfo = {
      ...clientInfo,
      corporate_structure: updatedValues,
    };
    
    setClientInfo(updatedClientInfo);
    // Logs de debugging removidos - el fix está funcionando
    // Notificar al padre sobre los cambios
    if (onDataChange) {
      onDataChange(updatedClientInfo);
    }
  };

  const handleSave = (shouldContinue: boolean) => {
    if (pendingAssetUploadsRef.current > 0) {
      return;
    }

    const { officer, management_id: rawManagementId, ...basicDataForPayload } =
      basicDataFormik.values as basicData & {
        officer?: string;
        management_id?: number | string;
      };

    // Construir el objeto category_type completo si existe category_type_id
    const categoryType = clientInfo.category_type_id 
      ? categoryTypes?.find(ct => ct.id === clientInfo.category_type_id)
      : undefined;

    const basicDataForSave = buildBasicDataForApi(basicDataForPayload);
    const parsedManagementId =
      rawManagementId === '' || rawManagementId === undefined || rawManagementId === null
        ? undefined
        : Number(rawManagementId);

    const updatedClientInfo = {
      ...clientInfo,
      basic_data: basicDataForSave,
      corporate_structure: societaryFormik.values,
      officer: basicDataFormik.values.officer,
      referrer_name: getReferrerNameCode(clientInfo.referrer_name), // Convertir a código para guardar
      category_type: categoryType, // Agregar el objeto completo
      management_id: parsedManagementId,
    };
    
    setClientInfo(updatedClientInfo);
    // Logs de debugging removidos - el fix está funcionando
    // Notificar al padre sobre los cambios
    if (onDataChange) {
      onDataChange(updatedClientInfo);
    }
    onSave({ clientInfo: updatedClientInfo, shouldContinue });
  };

  const isApplicationEditable = () => {
    return !isRejected && !isFinished && !isApproved && !isInCommittee;
  };

  const handleOnClickActivity = (e: activity) => {
    const foundActivity = economicActivities.find((item) => item.code === e.code);
    if (foundActivity) {
      basicDataFormik.setFieldValue('activity', String(foundActivity.code));
    }
  };

  const handleOnClickDateIcorporation = (e: any) => {
    const date = e || initialClientData.basic_data.date_incorporation;
    basicDataFormik.setFieldValue('date_incorporation', date);
  };

  const handleUploadAssets = async (
    index: number,
    event: React.ChangeEvent<HTMLInputElement>,
    socio: corporateStructureData
  ) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;

    // Validar tamaño del archivo (20MB máximo)
    if (file.size >= MAX_FILE_SIZE) {
      useToast.error({
        title: 'El tamaño del archivo no puede ser mayor de 20MB',
        id: 'save-toast',
        description: '',
        closeButton: true,
        autoClose: 5000,
      });
      // Limpiar el input de archivo
      event.target.value = '';
      return;
    }

    // Obtener el CI actualizado del formulario en lugar del objeto socio
    const currentCi = societaryFormik.values[index]?.ci;

    // Validar que el CI exista antes de permitir la carga
    if (!currentCi || currentCi.trim() === '') {
      setAssetUploadError({
        ...assetUploadError,
        [index]: 'Debe ingresar el documento del socio antes de cargar la manifestación de bienes',
      });
      // Limpiar el input de archivo
      event.target.value = '';
      return;
    }

    const basicErrors = validateBasicData(basicDataFormik.values);
    if (Object.keys(basicErrors).length > 0) {
      basicDataFormik.setErrors(basicErrors);
      await basicDataFormik.setTouched(
        Object.keys(basicErrors).reduce((acc, k) => ({ ...acc, [k]: true }), {}),
      );
      const msg =
        'Debe completar los datos generales obligatorios antes de adjuntar la manifestación de bienes';
      setAssetUploadError((prev) => ({ ...prev, [index]: msg }));
      useToast.error({ title: msg, id: 'save-toast', description: '', closeButton: true, autoClose: 5000 });
      event.target.value = '';
      return;
    }

    // Limpiar error previo si existe
    setAssetUploadError({
      ...assetUploadError,
      [index]: '',
    });

    setUploadingAssetIndex(index);
    pendingAssetUploadsRef.current += 1;
    setPendingAssetUploadsCount(pendingAssetUploadsRef.current);

    try {
      // 0. Primero guardar el cliente para asegurar que existe en la BD (solo la primera vez)
      // Estrategia simple: usar SOLO el ID del cliente para verificar si existe en la BD
      // - Si el cliente tiene ID válido (existe en BD) → NO llamar al endpoint
      // - Si el cliente NO tiene ID válido (es nuevo) → llamar al endpoint
      
      // Verificar si el cliente existe en la BD usando SOLO el ID
      // Si el ID no está disponible, verificar id_application como indicador de que la carpeta ya existe
      const idToCheck = basicData?.id || clientInfo.id;
      const idValue = idToCheck ? String(idToCheck) : '';
      // El cliente existe en la BD si tiene un ID válido (no vacío, no '0', no '-1')
      const hasValidClientId = idValue !== '' && idValue !== '0' && idValue !== '-1' && idValue.trim() !== '';
      
      // Si el ID no está disponible, verificar si tiene id_application (carpeta ya guardada)
      const hasApplicationId = basicData?.id_application !== undefined && basicData?.id_application !== null && basicData?.id_application !== 0;
      
      // El cliente existe si tiene ID válido O si tiene id_application (carpeta en revisión)
      const clientExistsInDb = hasValidClientId || hasApplicationId;
      
      // Guardar SOLO si el cliente NO existe en la BD
      const shouldSave = !clientExistsInDb;
      
      if (shouldSave) {
        const { officer, management_id: rawMidSave, ...basicRest } = basicDataFormik.values as basicData & {
          officer?: string;
          management_id?: number | string;
        };
        const parsedMidSave =
          rawMidSave === '' || rawMidSave === undefined || rawMidSave === null
            ? undefined
            : Number(rawMidSave);
        const clientDataToSave = {
          ...clientInfo,
          basic_data: buildBasicDataForApi(basicRest),
          corporate_structure: societaryFormik.values,
          officer: basicDataFormik.values.officer,
          referrer_name: getReferrerNameCode(clientInfo.referrer_name), // Convertir a código para guardar
          management_id: parsedMidSave,
        };

        await saveClientInfo(clientDataToSave, clientInfo.cuit, 0);
        setHasClientBeenSaved(true);
      }

      // 1. Analizar con IA (flujo actual)
      const formDataForAI = new FormData();
      formDataForAI.append('file', file);
      const aiResponse = await loadAssets(formDataForAI, index);

      // 2. Guardar archivo físico
      const formDataForStorage = new FormData();
      formDataForStorage.append('file', file);
      formDataForStorage.append('type', 'ASSET_DECLARATION');
      formDataForStorage.append(
        'fileName',
        `asset-${currentCi}-${Date.now()}`
      );
      formDataForStorage.append('isBasicData', 'false');

      const storageResponse = await saveClientDocument(
        clientInfo.cuit,
        formDataForStorage
      );

      // Verificar si el guardado del archivo falló
      if (!storageResponse.success) {
        let errorMessage = 'Error al cargar el archivo';
        
        // Verificar si hay un mensaje válido en la respuesta
        if ('message' in storageResponse && storageResponse.message) {
          const message = storageResponse.message;
          // Si el mensaje es un string válido y no es "[object Object]", usarlo
          if (typeof message === 'string' && message.trim() !== '' && message !== '[object Object]') {
            errorMessage = message;
          }
        }
        
        // Si es error 413 (Payload Too Large), usar mensaje específico
        if ('status' in storageResponse && storageResponse.status === 413) {
          errorMessage = 'El archivo es demasiado grande. Por favor, intente con un archivo más pequeño.';
        } else {
          errorMessage = toUserSafeUploadMessage(errorMessage);
        }
        
        useToast.error({
          title: errorMessage,
          id: 'save-toast',
          description: '',
          closeButton: true,
          autoClose: 5000,
        });
        setAssetUploadError({
          ...assetUploadError,
          [index]: errorMessage,
        });
        return;
      }

      // 3. Actualizar ambos valores
      if (aiResponse.success && storageResponse.success) {
        societaryFormik.setFieldValue(
          `[${index}].assets`,
          aiResponse.data.assets
        );
        societaryFormik.setFieldValue(
          `[${index}].assets_file_path`,
          storageResponse.data
        );
        societaryFormik.setFieldValue(`[${index}].value`, aiResponse.data.value);

        socio.assets = aiResponse.data.assets;
        socio.assets_file_path = storageResponse.data;
        socio.value = aiResponse.data.value;
      }
    } catch (error: unknown) {
      console.error('Error uploading asset:', error);
      let errorMessage = 'Error al subir el archivo';
      
      if (error && typeof error === 'object') {
        // Verificar si es un error 413 (Payload Too Large)
        if ('response' in error && error.response && typeof error.response === 'object') {
          const response = error.response as any;
          if (response.status === 413) {
            errorMessage = 'El archivo es demasiado grande. Por favor, intente con un archivo más pequeño.';
          } else if (typeof response?.data?.message === 'string') {
            errorMessage = toUserSafeUploadMessage(response.data.message);
          } else if (typeof (error as { message?: string }).message === 'string') {
            errorMessage = toUserSafeUploadMessage((error as { message: string }).message);
          }
        } else if (typeof (error as { message?: string }).message === 'string') {
          errorMessage = toUserSafeUploadMessage((error as { message: string }).message);
        }
      }
      
      useToast.error({
        title: errorMessage,
        id: 'save-toast',
        description: '',
        closeButton: true,
        autoClose: 5000,
      });
      setAssetUploadError({
        ...assetUploadError,
        [index]: errorMessage,
      });
    } finally {
      pendingAssetUploadsRef.current = Math.max(0, pendingAssetUploadsRef.current - 1);
      setPendingAssetUploadsCount(pendingAssetUploadsRef.current);
      setUploadingAssetIndex(null);
    }
  };

  const handleViewAsset = (fileName: string) => {
    downloadClientDocument(clientInfo.cuit, fileName).then((response) => {
      if (response.success) {
        const fileBlob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(fileBlob);
        window.open(url, '_blank');
      }
    });
  };

  const rendererManifestacionBienesFile = (
    index: number,
    socio: corporateStructureData
  ) => {
    const assets = societaryFormik.values[index]?.assets;
    const isUploading = uploadingAssetIndex === index;

    // Si ya tiene assets, mostrar input editable
    if (assets) {
      return (
        <Input
          label={index === 0 ? 'Manifestación de bienes' : ''}
          id='corporate_structure-input-assets'
          value={assets}
          onChange={(e) =>
            societaryFormik.setFieldValue(`[${index}].assets`, e.target.value)
          }
          disabled={!isApplicationEditable()}
        />
      );
    }

    // Si no tiene assets, mostrar input de archivo con spinner centrado cuando está cargando
    // Usar el valor actualizado del formulario en lugar del objeto socio
    const currentCi = societaryFormik.values[index]?.ci;
    const hasCi = currentCi && currentCi.trim() !== '';
    const errorMessage = assetUploadError[index];

    return (
      <div style={{ position: 'relative', width: '100%' }}>
        <div style={{ opacity: isUploading ? 0 : 1, pointerEvents: isUploading ? 'none' : 'auto' }}>
          <Input
            id={`file-input-asset-${index}`}
            label={index === 0 ? 'Manifestación de bienes' : ''}
            type='file'
            size='md'
            icons={!isUploading ? { iconRight: 'upload_outline' } : undefined}
            onChange={(event) => {
              handleUploadAssets(index, event, socio);
            }}
            accept='.pdf'
            disabled={!isApplicationEditable() || isUploading || !hasCi}
          />
          {errorMessage && (
            <Span variant='danger' type='2'>
              {errorMessage}
            </Span>
          )}
          {!hasCi && !errorMessage && (
            <Span variant='danger' type='2'>
              Debe ingresar el documento del socio antes de cargar la manifestación de bienes
            </Span>
          )}
        </div>
        {isUploading && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
              zIndex: 10,
            }}
          >
            <Spinner size='md' variant='primary' />
          </div>
        )}
      </div>
    );
  };

  const renderComposicionSocietaria = () => {
    return clientInfo.corporate_structure?.map(
      (socio: corporateStructureData, index) => (
        <Card
          key={socio.id}
          display='flex'
          gap='12px'
          margin={{ bottom: '2' }}
        >
            <Card width='-webkit-fill-available'>
              <Input
                label={
                  index === 0
                    ? isUruguayEnabled
                      ? 'Cédula'
                      : 'Documento'
                    : ''
                }
                id='corporate_structure-input-ci'
                value={societaryFormik.values[index]?.ci}
                onlyNumbers
                maxLength={
                  isUruguayEnabled
                    ? getUruguayDocumentMaxLength('Cedula')
                    : 11
                }
                onChange={(e) => {
                  societaryFormik.setFieldValue(`[${index}].ci`, e.target.value);
                  // Limpiar error de carga de asset si existe cuando se ingresa el CI
                  if (assetUploadError[index]) {
                    setAssetUploadError({
                      ...assetUploadError,
                      [index]: '',
                    });
                  }
                }}
                disabled={!isApplicationEditable()}
              />
              <Span variant='danger' type='2'>
                {societaryFormik.errors[index]?.ci}
              </Span>
            </Card>
            <Card width='-webkit-fill-available'>
              <Input
                label={index === 0 ? 'Nombre' : ''}
                id='corporate_structure-input-name'
                value={societaryFormik.values[index]?.first_name}
                type='text'
                onChange={(e) =>
                  societaryFormik.setFieldValue(
                    `[${index}].first_name`,
                    e.target.value.replace(/[0-9]/g, '')
                  )
                }
                disabled={!isApplicationEditable()}
              />
              <Span variant='danger' type='2'>
                {societaryFormik.errors[index]?.first_name}
              </Span>
            </Card>
            <Card width='50%'>
              <Input
                label={index === 0 ? 'Participación' : ''}
                id='corporate_structure-input-ownership_interest'
                inputStatus={
                  societaryFormik.errors[index]?.ownership_interest
                    ? 'error'
                    : 'normal'
                }
                value={societaryFormik.values[index]?.ownership_interest}
                onChange={(e) =>
                  societaryFormik.setFieldValue(
                    `[${index}].ownership_interest`,
                    parseFloat(e.target.value) || 0
                  )
                }
                disabled={!isApplicationEditable()}
              />
            </Card>

            <Input
              label={index === 0 ? 'Cargo' : ''}
              id='corporate_structure-input-position'
              value={societaryFormik.values[index]?.position}
              onChange={(e) =>
                societaryFormik.setFieldValue(
                  `[${index}].position`,
                  e.target.value
                )
              }
              disabled={!isApplicationEditable()}
            />
            {!isUruguayEnabled && (
              <Card width='-webkit-fill-available'>
                {rendererManifestacionBienesFile(index, socio)}
              </Card>
            )}
            {!isUruguayEnabled && (
              <NumericFormat
                label={index === 0 ? 'Valor' : ''}
                id='corporate_structure-input-value'
                value={societaryFormik.values[index]?.value ?? ''}
                thousandSeparator='.'
                decimalSeparator=','
                onValueChange={(values) =>
                  societaryFormik.setFieldValue(
                    `[${index}].value`,
                    values.value === '' ? '' : values.floatValue
                  )
                }
                customInput={Input}
                disabled={!isApplicationEditable()}
                decimalScale={2}
                allowNegative={false}
              />
            )}
            <Card
              display='flex'
              gap='8px'
              alignItems='flex-start'
              margin={index === 0 ? { top: '4' } : undefined}
            >
              {!isUruguayEnabled &&
                societaryFormik.values[index]?.assets_file_path && (
                <Button
                  variant='secondary'
                  size='md'
                  onClick={() =>
                    handleViewAsset(
                      societaryFormik.values[index]?.assets_file_path || ''
                    )
                  }
                  disabled={!isApplicationEditable()}
                >
                  <Icon
                    name='view_outline'
                    variant='neutral'
                    height={44}
                    cursor='pointer'
                  />
                </Button>
              )}
              <Button
                size='md'
                variant='secondary'
                disabled={
                  (index === 0 &&
                    clientInfo.corporate_structure.length === 1) ||
                  !isApplicationEditable()
                }
                onClick={() => removeCorporateMember(index)}
              >
                <Icon name='delete_outline' variant='neutral' height={44} cursor='pointer' />
              </Button>
            </Card>
          </Card>
        )
    );
  };

  return (
    <Card>
      <Modal
        isOpen={pendingManagementId !== null}
        onClose={() => setPendingManagementId(null)}
        title='Cambiar Gerencia'
        primaryText='Confirmar'
        secondaryText='Cancelar'
        primaryVariant='primary'
        secondaryVariant='secondary'
        loadingPrimaryBtn={isApplyingManagementChange}
        onSecondaryAction={() => setPendingManagementId(null)}
        onPrimaryAction={async () => {
          setIsApplyingManagementChange(true);
          try {
            await applyManagementId(pendingManagementId ?? undefined);
            setPendingManagementId(null);
          } finally {
            setIsApplyingManagementChange(false);
          }
        }}
      >
        <P type='1' variant='neutral'>
          {MANAGEMENT_CHANGE_WARNING}
        </P>
      </Modal>
      <Card display='grid' gap='8px' margin={{ bottom: '4' }}>
        <P
          type='1'
          variant='neutral-1'
          withMargin={false}
          weight='bold'
          modifier='strong'
        >
          Datos del cliente
        </P>
        <P type='1' withMargin={false} variant='neutral' modifier='regular'>
          {isUruguayEnabled
            ? 'Seleccioná el tipo de documento y escribí el número para poder comenzar la carga al sistema.'
            : 'Escribí el CUIT del cliente para poder comenzar la carga al sistema.'}
        </P>
      </Card>
      <form onSubmit={cuitFormik.handleSubmit}>
        {isUruguayEnabled && (
          <Card display='flex' gap='24px' margin={{ bottom: '3' }} alignItems='center'>
            <RadioButton
              id='document-type-rut'
              name='documentSearchType'
              value='RUT'
              label='RUT'
              checked={documentSearchType === 'RUT'}
              disabled={hasClientBeenSaved}
              onChange={() => {
                setDocumentSearchType('RUT');
                cuitFormik.setFieldValue('cuit', '');
                setErrorMsg('');
                setIsApplicationOnCourse(false);
                setIsCuitValid(true);
                setValidatedBasicData(true);
              }}
            />
            <RadioButton
              id='document-type-cedula'
              name='documentSearchType'
              value='Cedula'
              label='Cédula'
              checked={documentSearchType === 'Cedula'}
              disabled={hasClientBeenSaved}
              onChange={() => {
                setDocumentSearchType('Cedula');
                cuitFormik.setFieldValue('cuit', '');
                setErrorMsg('');
                setIsApplicationOnCourse(false);
                setIsCuitValid(true);
                setValidatedBasicData(true);
              }}
            />
          </Card>
        )}
        <Card display='flex' width='30vw' gap='32px' alignItems='center'>
          <Input
            id='client-input-cuit'
            placeholder={
              isUruguayEnabled
                ? documentSearchType === 'RUT'
                  ? 'Ingresá un RUT'
                  : 'Ingresá una Cédula'
                : 'Ingresá un CUIT'
            }
            inputStatus={errorMsg !== '' ? 'error' : 'normal'}
            size='md'
            icons={{ iconLeft: 'search_outline' }}
            autocomplete='off'
            onChange={(e) => {
              const formattedValue = isUruguayEnabled
                ? formatDocumentForSearch(e.target.value, true).slice(
                    0,
                    getUruguayDocumentMaxLength(documentSearchType)
                  )
                : formatDocumentForSearch(e.target.value, false);
              cuitFormik.setFieldValue('cuit', formattedValue);

              const rawNewCuit = formattedValue.replace(/-/g, '');
              const rawSearchedCuit = searchedCuit.replace(/-/g, '');
              if (rawNewCuit !== rawSearchedCuit && !hasClientBeenSaved) {
                setIsApplicationOnCourse(false);
                setIsCuitValid(true);
                setValidatedBasicData(true);
              }
            }}
            onPaste={(e) => {
              e.preventDefault();
              const pasted = e.clipboardData.getData('Text').replace(/\D/g, '');
              let formatted: string;
              if (isUruguayEnabled) {
                formatted = pasted.slice(
                  0,
                  getUruguayDocumentMaxLength(documentSearchType)
                );
              } else {
                formatted = pasted;
                if (pasted.length === 11) {
                  formatted = `${pasted.slice(0, 2)}-${pasted.slice(2, 10)}-${pasted.slice(10, 11)}`;
                }
              }
              cuitFormik.setFieldValue('cuit', formatted);

              const rawNewCuit = formatted.replace(/-/g, '');
              const rawSearchedCuit = searchedCuit.replace(/-/g, '');
              if (rawNewCuit !== rawSearchedCuit && !hasClientBeenSaved) {
                setIsApplicationOnCourse(false);
                setIsCuitValid(true);
                setValidatedBasicData(true);
              }
            }}
            message={errorMsg}
            value={cuitFormik.values.cuit}
            disabled={hasClientBeenSaved}
          />
          <Button
            variant='tertiary-filled'
            size='sm'
            style={{ minWidth: '80px', justifyContent: 'center' }}
            disabled={
              hasClientBeenSaved || 
              isApplicationOnCourse || 
              isSearching || 
              !cuitFormik.values.cuit || 
              (isUruguayEnabled
                ? !isValidUruguayDocumentLength(
                    cuitFormik.values.cuit,
                    documentSearchType
                  )
                : cuitFormik.values.cuit.replace(/-/g, '').length !== 11)
            }
            onClick={cuitFormik.handleSubmit}
          >
            {isSearching ? <Spinner size='sm' variant='neutral' /> : 'Buscar'}
          </Button>
        </Card>
      </form>
      <Card display='grid' gap='8px' margin={{ bottom: '4', top: '4' }}>
        <P
          type='1'
          variant='neutral-1'
          withMargin={false}
          weight='bold'
          modifier='strong'
        >
          Datos generales
        </P>
        <P type='1' withMargin={false} variant='neutral' modifier='regular'>
          {isUruguayEnabled
            ? 'Revisá que los datos sean correctos. Podés modificarlos.'
            : 'Revisá que los datos del CUIT sean correctos. Podés modificarlos.'}
        </P>
      </Card>
      {(isClientFound || isApplicationOnCourse) && !hasPendingApplication ? (
        <Card margin={{ top: '4' }}>
          <form onSubmit={basicDataFormik.handleSubmit}>
            <Card>
              <Card
                display={isUruguayEnabled ? 'grid' : 'flex'}
                gap='24px'
                alignItems='flex-start'
                style={isUruguayEnabled ? UY_DATOS_GENERALES_GRID_3 : undefined}
              >
                <Card width={isUruguayEnabled ? '100%' : '35vw'}>
                  <Input
                    label='Razón social *'
                    id='client-input-company_name'
                    value={basicDataFormik.values.company_name}
                    maxLength={300}
                    type='text'
                    onChange={(e) =>
                      basicDataFormik.setFieldValue(
                        'company_name',
                        e.target.value
                      )
                    }
                    disabled={false}
                  />
                  <Span variant='danger' type='2'>
                    {basicDataFormik.errors.company_name}
                  </Span>
                </Card>
                <Card width={isUruguayEnabled ? '100%' : '25vw'}>
                  <DropDown
                    id='client-legal_entity-dropdown'
                    label='Personería *'
                    items={personeriaItems}
                    size='md'
                    title={basicDataFormik.values.legal_entity}
                    onChange={(e) =>
                      basicDataFormik.setFieldValue(
                        'legal_entity',
                        personeriaItems.find((item) => item.label === e)?.label
                      )
                    }
                    disabled={!isApplicationEditable()}
                  ></DropDown>
                  <Span variant='danger' type='2'>
                    {basicDataFormik.errors.legal_entity}
                  </Span>
                </Card>
                <Card width={isUruguayEnabled ? '100%' : '25vw'}>
                  <P withMargin={false} variant='neutral-1' type='4'>
                    {isUruguayCountry ? 'Fecha de constitución' : 'Fecha de constitución *'}
                  </P>
                  <Card margin={{ top: '3' }} width='100%'>
                    <DatePicker
                      disabled={false}
                      defaultValue={normalizeToIsoDate(basicDataFormik.values.date_incorporation) || ''}
                      placeholder='Seleccionar fecha'
                      displayFormat='DD/MM/AA'
                      onChange={handleOnClickDateIcorporation}
                    />
                  </Card>
                  <Span variant='danger' type='2'>
                    {basicDataFormik.errors.date_incorporation}
                  </Span>
                </Card>
              </Card>
              {isUruguayEnabled ? (
                <Card
                  display='grid'
                  margin={{ top: '3' }}
                  gap='24px'
                  alignItems='flex-start'
                  style={UY_DATOS_GENERALES_GRID_3}
                >
                  {isClientCategoryTypeEnabled && (
                    <Card width='100%'>
                      <DropDown
                        id='client-category_type-dropdown-uy'
                        label='Rubro *'
                        title={getSelectedCategoryType()}
                        items={categoryTypeItems}
                        onChange={handleCategoryTypeChange}
                        size='md'
                        disabled={!isApplicationEditable() || isLoadingCategoryTypes}
                      />
                      <Span variant='danger' type='2'>
                        {basicDataFormik.touched.category_type_id && (basicDataFormik.errors as any).category_type_id}
                      </Span>
                    </Card>
                  )}
                  <Card width='100%' display='grid' gap='8px'>
                    <DropDown
                      id='referrer-name-dropdown-uy'
                      label='Referido/Vendedor'
                      items={referrerNameItems}
                      size='md'
                      title={getReferrerNameLabel(clientInfo.referrer_name) || 'Seleccionar referido/vendedor'}
                      onChange={(selectedName) => {
                        const selectedReferrer = referrerNames.find(rn => rn.name === selectedName);
                        setClientInfo((prev) => ({
                          ...prev,
                          referrer_name: selectedReferrer || selectedName,
                        }));
                      }}
                      searchable={true}
                      searchPlaceholder='Buscar productor...'
                      disabled={!isApplicationEditable() || isLoadingReferrerNames}
                    />
                  </Card>
                  {!isManagementSelectionEnabled ? (
                    <Card width='100%'>
                      <DropDown
                        id='officers-dropdown-uy'
                        label='Oficial'
                        items={officers}
                        size='md'
                        title={
                          officers.find(
                            (item) => item.code === basicDataFormik.values.officer
                          )?.label || 'Seleccionar oficial'
                        }
                        onChange={(e) =>
                          basicDataFormik.setFieldValue(
                            'officer',
                            officers.find((item) => item.label === e)?.code
                          )
                        }
                        disabled={!isApplicationEditable() || isLoadingOfficers}
                      />
                    </Card>
                  ) : (
                    <Card width='100%'>
                      <DropDown
                        id='management-dropdown-uy'
                        label='Gerencia *'
                        items={managementDropdownItems}
                        size='md'
                        title={(() => {
                          const mid = (basicDataFormik.values as basicData & { management_id?: number | string })
                            .management_id;
                          if (mid === '' || mid === undefined || mid === null) {
                            return 'Seleccionar gerencia';
                          }
                          return managements.find((item) => item.id === Number(mid))?.label ?? 'Seleccionar gerencia';
                        })()}
                        onChange={handleManagementSelect}
                        disabled={!isApplicationEditable() || isLoadingManagements}
                      />
                      <Span variant='danger' type='2'>
                        {(basicDataFormik.errors as { management_id?: string }).management_id}
                      </Span>
                    </Card>
                  )}
                </Card>
              ) : (
              <Card display='flex' margin={{ top: '3' }} gap='24px' alignItems='flex-start'>
                {!isUruguayEnabled && (
                <Card width='30vw'>
                  <SearchableSelect
                    key={`activity-${basicDataFormik.values.activity || 'empty'}-${isClientFound}-${getActivityLabel}`}
                    disabled={false}
                    id='client-search-activity'
                    label='Actividad económica'
                    options={economicActivities}
                    value={getActivityLabel}
                    onChange={(e) => {
                      // Solo actualizar si e es un número (código de actividad seleccionado)
                      // Si es un string, es porque el usuario está escribiendo, no actualizar
                      if (typeof e === 'number' || (typeof e === 'string' && !isNaN(Number(e)) && e.trim() !== '')) {
                        const activityCode = typeof e === 'number' ? e : Number(e);
                        const foundActivity = economicActivities.find((item) => item.code === activityCode);
                        if (foundActivity) {
                          basicDataFormik.setFieldValue('activity', String(foundActivity.code));
                        }
                      }
                    }}
                    onClick={handleOnClickActivity}
                  ></SearchableSelect>
                  <Span variant='danger' type='2'>
                    {basicDataFormik.errors.activity}
                  </Span>
                </Card>
                )}
                {!isUruguayEnabled && (
                <Card width='30vw'>
                  <DropDown
                    id='client-iva_status-dropdown'
                    label='Condición frente al IVA'
                    items={ivaStatuses}
                    size='md'
                    title={
                      ivaStatuses.find(
                        (item) =>
                          item.code === basicDataFormik.values.iva_status
                      )?.label
                    }
                    onChange={(e) =>
                      basicDataFormik.setFieldValue(
                        'iva_status',
                        ivaStatuses.find((item) => item.label === e)?.code
                      )
                    }
                    disabled={!isApplicationEditable()}
                  ></DropDown>
                  <Span variant='danger' type='2'>
                    {basicDataFormik.errors.iva_status}
                  </Span>
                </Card>
                )}
                {isClientCategoryTypeEnabled && (
                  <Card>
                    <Card width='30vw'>
                      <DropDown
                        id='client-category_type-dropdown'
                        label='Rubro *'
                        title={getSelectedCategoryType()}
                        items={categoryTypeItems}
                        onChange={handleCategoryTypeChange}
                        size='md'
                        disabled={!isApplicationEditable() || isLoadingCategoryTypes}
                      />
                      <Span variant='danger' type='2'>
                        {basicDataFormik.touched.category_type_id && (basicDataFormik.errors as any).category_type_id}
                      </Span>
                    </Card>
                  </Card>
                )} 
                {!isClientCategoryTypeEnabled && (
                  <>
                    <Card display='flex' gap='24px' alignItems='flex-start'>
                      <Card width='20vw' display='grid' gap='8px'>
                        <Card display='flex' gap='8px' alignItems='flex-end'>
                          <Card width='-webkit-fill-available'>
                            <DropDown
                              id='referrer-name-dropdown'
                              label='Referido/Vendedor'
                              items={referrerNameItems}
                              size='md'
                              title={getReferrerNameLabel(clientInfo.referrer_name) || 'Seleccionar referido/vendedor'}
                              onChange={(selectedName) => {
                                const selectedReferrer = referrerNames.find(rn => rn.name === selectedName);
                                setClientInfo((prev) => ({
                                  ...prev,
                                  referrer_name: selectedReferrer || selectedName,
                                }));
                              }}
                              searchable={true}
                              searchPlaceholder='Buscar productor...'
                              disabled={!isApplicationEditable() || isLoadingReferrerNames}
                            />
                          </Card>
                          {hasPermission && (
                            <Button
                              variant='secondary'
                              size='md'
                              iconAlign='center'
                              onClick={() => setIsReferrersModalOpen(true)}
                              disabled={!isApplicationEditable() || isLoadingReferrerNames}
                              style={{
                                width: '52px',
                                height: '52px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: '52px',
                                maxHeight: '52px',
                                flexShrink: 0,
                                marginBottom: '0',
                              }}
                              title='Gestionar referidos/vendedores'
                            >
                              <Icon 
                                name='settings_outline' 
                                variant='neutral' 
                                modifier='strong'
                                width={24} 
                                height={24}
                                cursor='pointer'
                              />
                            </Button>
                          )}
                        </Card>
                        <ReferrersManagementModal
                          isOpen={isReferrersModalOpen}
                          onClose={() => {
                            setIsReferrersModalOpen(false);
                            refreshReferrerNames();
                          }}
                          onReferrerUpdated={() => {
                            refreshReferrerNames();
                          }}
                        />
                      </Card>
                      {!isManagementSelectionEnabled ? (
                        <Card width='15vw'>
                          <DropDown
                            id='officers-dropdown'
                            label='Oficial'
                            items={officers}
                            size='md'
                            title={
                              officers.find(
                                (item) => item.code === basicDataFormik.values.officer
                              )?.label
                            }
                            onChange={(e) =>
                              basicDataFormik.setFieldValue(
                                'officer',
                                officers.find((item) => item.label === e)?.code
                              )
                            }
                            disabled={!isApplicationEditable() || isLoadingOfficers}
                          ></DropDown>
                        </Card>
                      ) : (
                        <Card width='30vw'>
                          <DropDown
                            id='management-dropdown-ncat'
                            label='Gerencia *'
                            items={managementDropdownItems}
                            size='md'
                            title={(() => {
                              const mid = (basicDataFormik.values as basicData & { management_id?: number | string })
                                .management_id;
                              if (mid === '' || mid === undefined || mid === null) {
                                return 'Seleccionar gerencia';
                              }
                              return managements.find((item) => item.id === Number(mid))?.label ?? 'Seleccionar gerencia';
                            })()}
                            onChange={handleManagementSelect}
                            disabled={!isApplicationEditable() || isLoadingManagements}
                          />
                          <Span variant='danger' type='2'>
                            {(basicDataFormik.errors as { management_id?: string }).management_id}
                          </Span>
                        </Card>
                      )}
                    </Card>
                    {isEconomicGroupEnabled && (
                      <Card display='flex' gap='24px' alignItems='flex-start' margin={{ top: '3' }}>
                        <Card width='20vw' display='grid' gap='8px'>
                          <Card display='flex' gap='8px' alignItems='flex-end'>
                            <Card width='-webkit-fill-available'>
                              <DropDown
                                id='economic-group-dropdown'
                                label='Grupo económico'
                                items={economicGroupItems}
                                size='md'
                                title={
                                  economicGroups.find((g) => g.code === clientInfo.economic_group)?.name ||
                                  clientInfo.economic_group ||
                                  'Seleccionar grupo económico'
                                }
                                onChange={(selectedName) => {
                                  const selectedGroup = economicGroups.find((g) => g.name === selectedName);
                                  setClientInfo((prev) => ({
                                    ...prev,
                                    economic_group: selectedGroup ? selectedGroup.code : selectedName,
                                  }));
                                }}
                                searchable={true}
                                searchPlaceholder='Buscar grupo económico...'
                                disabled={!isApplicationEditable() || isLoadingEconomicGroups}
                              />
                            </Card>
                            {hasPermission && (
                              <Button
                                variant='secondary'
                                size='md'
                                iconAlign='center'
                                onClick={() => setIsEconomicGroupModalOpen(true)}
                                disabled={!isApplicationEditable() || isLoadingEconomicGroups}
                                style={{
                                  width: '52px',
                                  height: '52px',
                                  padding: '0',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  minHeight: '52px',
                                  maxHeight: '52px',
                                  flexShrink: 0,
                                  marginBottom: '0',
                                }}
                                title='Gestionar grupos económicos'
                              >
                                <Icon
                                  name='settings_outline'
                                  variant='neutral'
                                  modifier='strong'
                                  width={24}
                                  height={24}
                                  cursor='pointer'
                                />
                              </Button>
                            )}
                          </Card>
                          <EconomicGroupManagementModal
                            isOpen={isEconomicGroupModalOpen}
                            onClose={() => {
                              setIsEconomicGroupModalOpen(false);
                              refreshEconomicGroups();
                            }}
                            onGroupUpdated={() => {
                              refreshEconomicGroups();
                            }}
                          />
                        </Card>
                      </Card>
                    )}
                  </>
                )}
              </Card>
              )}
              {!isUruguayEnabled && isClientCategoryTypeEnabled && (
                  <>
                    <Card display='flex' margin={{ top: '3' }} gap='24px' alignItems='flex-start'>
                      <Card width='50vw' display='grid' gap='8px'>
                        <Card display='flex' gap='8px' alignItems='flex-end'>
                          <Card width='-webkit-fill-available'>
                            <DropDown
                              id='referrer-name-dropdown'
                              label='Referido/Vendedor'
                              items={referrerNameItems}
                              size='md'
                              title={getReferrerNameLabel(clientInfo.referrer_name) || 'Seleccionar referido/vendedor'}
                              onChange={(selectedName) => {
                                const selectedReferrer = referrerNames.find(rn => rn.name === selectedName);
                                setClientInfo((prev) => ({
                                  ...prev,
                                  referrer_name: selectedReferrer || selectedName,
                                }));
                              }}
                              searchable={true}
                              searchPlaceholder='Buscar productor...'
                              disabled={!isApplicationEditable() || isLoadingReferrerNames}
                            />
                          </Card>
                          {hasPermission && (
                            <Button
                              variant='secondary'
                              size='md'
                              iconAlign='center'
                              onClick={() => setIsReferrersModalOpen(true)}
                              disabled={!isApplicationEditable() || isLoadingReferrerNames}
                              style={{
                                width: '52px',
                                height: '52px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: '52px',
                                maxHeight: '52px',
                                flexShrink: 0,
                                marginBottom: '0',
                              }}
                              title='Gestionar referidos/vendedores'
                            >
                              <Icon 
                                name='settings_outline' 
                                variant='neutral' 
                                modifier='strong'
                                width={24} 
                                height={24}
                                cursor='pointer'
                              />
                            </Button>
                          )}
                        </Card>
                        <ReferrersManagementModal
                          isOpen={isReferrersModalOpen}
                          onClose={() => {
                            setIsReferrersModalOpen(false);
                            refreshReferrerNames();
                          }}
                          onReferrerUpdated={() => {
                            refreshReferrerNames();
                          }}
                        />
                      </Card>
                      {!isManagementSelectionEnabled ? (
                        <Card width='50vw'>
                          <DropDown
                            id='officers-dropdown'
                            label='Oficial'
                            items={officers}
                            size='md'
                            title={
                              officers.find(
                                (item) => item.code === basicDataFormik.values.officer
                              )?.label
                            }
                            onChange={(e) =>
                              basicDataFormik.setFieldValue(
                                'officer',
                                officers.find((item) => item.label === e)?.code
                              )
                            }
                            disabled={!isApplicationEditable() || isLoadingOfficers}
                          ></DropDown>
                        </Card>
                      ) : (
                        <Card width='50vw'>
                          <DropDown
                            id='management-dropdown-cat'
                            label='Gerencia *'
                            items={managementDropdownItems}
                            size='md'
                            title={(() => {
                              const mid = (basicDataFormik.values as basicData & { management_id?: number | string })
                                .management_id;
                              if (mid === '' || mid === undefined || mid === null) {
                                return 'Seleccionar gerencia';
                              }
                              return managements.find((item) => item.id === Number(mid))?.label ?? 'Seleccionar gerencia';
                            })()}
                            onChange={handleManagementSelect}
                            disabled={!isApplicationEditable() || isLoadingManagements}
                          />
                          <Span variant='danger' type='2'>
                            {(basicDataFormik.errors as { management_id?: string }).management_id}
                          </Span>
                        </Card>
                      )}
                    </Card>
                    {isEconomicGroupEnabled && (
                      <Card display='flex' margin={{ top: '3' }} gap='24px' alignItems='flex-start'>
                        <Card width='50vw' display='grid' gap='8px'>
                          <Card display='flex' gap='8px' alignItems='flex-end'>
                            <Card width='-webkit-fill-available'>
                              <DropDown
                                id='economic-group-dropdown'
                                label='Grupo económico'
                                items={economicGroupItems}
                                size='md'
                                title={
                                  economicGroups.find((g) => g.code === clientInfo.economic_group)?.name ||
                                  clientInfo.economic_group ||
                                  'Seleccionar grupo económico'
                                }
                                onChange={(selectedName) => {
                                  const selectedGroup = economicGroups.find((g) => g.name === selectedName);
                                  setClientInfo((prev) => ({
                                    ...prev,
                                    economic_group: selectedGroup ? selectedGroup.code : selectedName,
                                  }));
                                }}
                                searchable={true}
                                searchPlaceholder='Buscar grupo económico...'
                                disabled={!isApplicationEditable() || isLoadingEconomicGroups}
                              />
                            </Card>
                            {hasPermission && (
                              <Button
                                variant='secondary'
                                size='md'
                                iconAlign='center'
                                onClick={() => setIsEconomicGroupModalOpen(true)}
                                disabled={!isApplicationEditable() || isLoadingEconomicGroups}
                                style={{
                                  width: '52px',
                                  height: '52px',
                                  padding: '0',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  minHeight: '52px',
                                  maxHeight: '52px',
                                  flexShrink: 0,
                                  marginBottom: '0',
                                }}
                                title='Gestionar grupos económicos'
                              >
                                <Icon
                                  name='settings_outline'
                                  variant='neutral'
                                  modifier='strong'
                                  width={24}
                                  height={24}
                                  cursor='pointer'
                                />
                              </Button>
                            )}
                          </Card>
                          <EconomicGroupManagementModal
                            isOpen={isEconomicGroupModalOpen}
                            onClose={() => {
                              setIsEconomicGroupModalOpen(false);
                              refreshEconomicGroups();
                            }}
                            onGroupUpdated={() => {
                              refreshEconomicGroups();
                            }}
                          />
                        </Card>
                      </Card>
                    )}
                  </>
                )}
              <Card
                display={isUruguayEnabled ? 'grid' : 'flex'}
                margin={{ top: '3' }}
                gap='24px'
                alignItems='flex-start'
                style={isUruguayEnabled ? UY_DATOS_GENERALES_GRID_3 : undefined}
              >
                <Card width={isUruguayEnabled ? '100%' : '30vw'}>
                  <Input
                    label='Domicilio fiscal *'
                    id='client-input-company_address'
                    value={basicDataFormik.values.company_address}
                    maxLength={500}
                    onChange={(e) =>
                      basicDataFormik.setFieldValue(
                        'company_address',
                        e.target.value
                      )
                    }
                    disabled={!isApplicationEditable()}
                  />
                  <Span variant='danger' type='2'>
                    {basicDataFormik.errors.company_address}
                  </Span>
                </Card>
                <Card width={isUruguayEnabled ? '100%' : '30vw'}>
                  <DropDown
                    id='client-state-dropdown'
                    label={isUruguayEnabled ? 'Departamento *' : regionLabel}
                    items={regionOptions}
                    size='md'
                    title={basicDataFormik.values.state || (isUruguayEnabled ? 'Seleccionar departamento' : undefined)}
                    onChange={(e) => {
                      const selected = regionOptions.find((item) => item.label === e)?.label || '';
                      basicDataFormik.setFieldValue('state', selected);
                      if (isUruguayEnabled) {
                        const localityItems = getUruguayLocalityItems(selected);
                        if (
                          basicDataFormik.values.city &&
                          !localityItems.some((item) => item.label === basicDataFormik.values.city)
                        ) {
                          basicDataFormik.setFieldValue('city', '');
                        }
                      }
                    }}
                    disabled={!isApplicationEditable()}
                  ></DropDown>
                  <Span variant='danger' type='2'>
                    {basicDataFormik.errors.state}
                  </Span>
                </Card>
                {isUruguayEnabled ? (
                  <Card
                    width='100%'
                    display='grid'
                    gap='24px'
                    alignItems='flex-start'
                    style={UY_LOCALITY_POSTAL_GRID}
                  >
                    <Card width='100%'>
                      <DropDown
                        id='client-city-dropdown'
                        label='Localidad *'
                        items={fiscalLocalityItems}
                        size='md'
                        title={basicDataFormik.values.city || 'Seleccionar localidad'}
                        onChange={(e) => basicDataFormik.setFieldValue('city', e)}
                        disabled={!isApplicationEditable() || !basicDataFormik.values.state}
                      />
                      <Span variant='danger' type='2'>
                        {basicDataFormik.errors.city}
                      </Span>
                    </Card>
                    <Card width='100%'>
                      <Input
                        label={isUruguayCountry ? 'Código Postal' : 'Código Postal *'}
                        id='client-input-postal_code'
                        value={basicDataFormik.values.postal_code || ''}
                        maxLength={20}
                        placeholder='Código postal'
                        onChange={(e) =>
                          basicDataFormik.setFieldValue('postal_code', e.target.value)
                        }
                        disabled={!isApplicationEditable()}
                      />
                      <Span variant='danger' type='2'>
                        {basicDataFormik.errors.postal_code}
                      </Span>
                    </Card>
                  </Card>
                ) : (
                <Card width='30vw'>
                  <Input
                    label='Localidad'
                    id='client-input-city'
                    value={basicDataFormik.values.city}
                    maxLength={300}
                    onChange={(e) =>
                      basicDataFormik.setFieldValue('city', e.target.value)
                    }
                    disabled={!isApplicationEditable()}
                  />
                  <Span variant='danger' type='2'>
                    {basicDataFormik.errors.city}
                  </Span>
                </Card>
                )}
              </Card>

              <Card
                display={isUruguayEnabled ? 'grid' : 'flex'}
                margin={{ top: '3' }}
                gap='24px'
                alignItems='flex-start'
                style={isUruguayEnabled ? UY_DATOS_GENERALES_GRID_3 : undefined}
              >
                <Card width={isUruguayEnabled ? '100%' : '30vw'}>
                  <Input
                    label='Domicilio legal'
                    id='client-input-legal_company_address'
                    value={basicDataFormik.values.legal_company_address || ''}
                    maxLength={500}
                    onChange={(e) =>
                      basicDataFormik.setFieldValue(
                        'legal_company_address',
                        e.target.value
                      )
                    }
                    disabled={!isApplicationEditable()}
                  />
                </Card>
                <Card width={isUruguayEnabled ? '100%' : '30vw'}>
                  <DropDown
                    id='client-legal_state-dropdown'
                    label={isUruguayEnabled ? 'Departamento legal' : 'Provincia legal'}
                    items={regionOptions}
                    size='md'
                    title={basicDataFormik.values.legal_state || (isUruguayEnabled ? 'Seleccionar departamento' : '')}
                    onChange={(e) => {
                      const selected = regionOptions.find((item) => item.label === e)?.label || '';
                      basicDataFormik.setFieldValue('legal_state', selected);
                      if (isUruguayEnabled) {
                        const localityItems = getUruguayLocalityItems(selected);
                        if (
                          basicDataFormik.values.legal_city &&
                          !localityItems.some((item) => item.label === basicDataFormik.values.legal_city)
                        ) {
                          basicDataFormik.setFieldValue('legal_city', '');
                        }
                      }
                    }}
                    disabled={!isApplicationEditable()}
                  ></DropDown>
                </Card>
                {isUruguayEnabled ? (
                  <Card
                    width='100%'
                    display='grid'
                    gap='24px'
                    alignItems='flex-start'
                    style={UY_LOCALITY_POSTAL_GRID}
                  >
                    <Card width='100%'>
                      <DropDown
                        id='client-legal_city-dropdown'
                        label='Localidad legal'
                        items={legalLocalityItems}
                        size='md'
                        title={basicDataFormik.values.legal_city || 'Seleccionar localidad'}
                        onChange={(e) => basicDataFormik.setFieldValue('legal_city', e)}
                        disabled={!isApplicationEditable() || !basicDataFormik.values.legal_state}
                      />
                    </Card>
                    <Card width='100%'>
                      <Input
                        label='Código Postal legal'
                        id='client-input-legal_postal_code'
                        value={basicDataFormik.values.legal_postal_code || ''}
                        maxLength={20}
                        placeholder='Código postal'
                        onChange={(e) =>
                          basicDataFormik.setFieldValue('legal_postal_code', e.target.value)
                        }
                        disabled={!isApplicationEditable()}
                      />
                    </Card>
                  </Card>
                ) : (
                <Card width='30vw'>
                  <Input
                    label='Localidad legal'
                    id='client-input-legal_city'
                    value={basicDataFormik.values.legal_city || ''}
                    maxLength={300}
                    onChange={(e) =>
                      basicDataFormik.setFieldValue('legal_city', e.target.value)
                    }
                    disabled={!isApplicationEditable()}
                  />
                </Card>
                )}
              </Card>

              {isUruguayEnabled && (
                <>
                  <Card display='grid' gap='8px' margin={{ bottom: '4', top: '4' }}>
                    <P type='1' variant='neutral-1' withMargin={false} weight='bold' modifier='strong'>
                      Proveedor del Estado
                    </P>
                  </Card>
                  <Card display='flex' gap='24px' alignItems='flex-start'>
                    {renderYesNoRadioGroup(
                      'Vínculo contractual *',
                      'contractual_link',
                      basicDataFormik.values.contractual_link,
                      (selected) => basicDataFormik.setFieldValue('contractual_link', selected),
                      basicDataFormik.errors.contractual_link as string | undefined,
                    )}
                    {renderYesNoRadioGroup(
                      'Persona políticamente expuesta *',
                      'politically_exposed',
                      basicDataFormik.values.politically_exposed,
                      (selected) => basicDataFormik.setFieldValue('politically_exposed', selected),
                      basicDataFormik.errors.politically_exposed as string | undefined,
                    )}
                    {renderYesNoRadioGroup(
                      'Registro Único de Proveedores del Estado (RUPE) *',
                      'state_supplier_registry',
                      basicDataFormik.values.state_supplier_registry,
                      (selected) => basicDataFormik.setFieldValue('state_supplier_registry', selected),
                      basicDataFormik.errors.state_supplier_registry as string | undefined,
                    )}
                  </Card>

                  <Card display='grid' gap='8px' margin={{ bottom: '4', top: '4' }}>
                    <P type='1' variant='neutral-1' withMargin={false} weight='bold' modifier='strong'>
                      Inscripción DGI
                    </P>
                  </Card>
                  <Card display='flex' gap='24px' alignItems='flex-start'>
                    <Card width='30vw'>
                      <P withMargin={false} variant='neutral-1' type='4'>
                        Fecha de vencimiento
                      </P>
                      <Card margin={{ top: '3', right: '3' }}>
                        <DatePicker
                          id='client-dgi-expiration-date'
                          disabled={!isApplicationEditable()}
                          defaultValue={normalizeToIsoDate(basicDataFormik.values.dgi_expiration_date ?? undefined) || ''}
                          placeholder='Seleccionar fecha'
                          displayFormat='DD/MM/AA'
                          onChange={(date: string) => basicDataFormik.setFieldValue('dgi_expiration_date', date)}
                        />
                      </Card>
                    </Card>
                    <Card width='30vw'>
                      <DropDown
                        id='client-dgi_status-dropdown'
                        label='Estado DGI *'
                        items={dgiStatusItems}
                        size='md'
                        title={basicDataFormik.values.dgi_status || 'Seleccionar estado DGI'}
                        onChange={(e) => basicDataFormik.setFieldValue('dgi_status', e)}
                        disabled={!isApplicationEditable()}
                      />
                      <Span variant='danger' type='2'>
                        {basicDataFormik.errors.dgi_status}
                      </Span>
                    </Card>
                    <Card width='30vw'>
                      <P withMargin={false} variant='neutral-1' type='4'>
                        Fecha de emisión
                      </P>
                      <Card margin={{ top: '3', right: '3' }}>
                        <DatePicker
                          id='client-dgi-issue-date'
                          disabled={!isApplicationEditable()}
                          defaultValue={normalizeToIsoDate(basicDataFormik.values.dgi_issue_date ?? undefined) || ''}
                          placeholder='Seleccionar fecha'
                          displayFormat='DD/MM/AA'
                          onChange={(date: string) => basicDataFormik.setFieldValue('dgi_issue_date', date)}
                        />
                      </Card>
                    </Card>
                  </Card>

                  <Card display='grid' gap='8px' margin={{ bottom: '4', top: '4' }}>
                    <P type='1' variant='neutral-1' withMargin={false} weight='bold' modifier='strong'>
                      Certificado común BPS
                    </P>
                  </Card>
                  <Card display='flex' gap='24px' alignItems='flex-start' margin={{ bottom: '4' }}>
                    <Card width='30vw'>
                      <P withMargin={false} variant='neutral-1' type='4'>
                        Fecha de vencimiento
                      </P>
                      <Card margin={{ top: '3', right: '3' }}>
                        <DatePicker
                          id='client-bps-expiration-date'
                          disabled={!isApplicationEditable()}
                          defaultValue={normalizeToIsoDate(basicDataFormik.values.bps_expiration_date ?? undefined) || ''}
                          placeholder='Seleccionar fecha'
                          displayFormat='DD/MM/AA'
                          onChange={(date: string) => basicDataFormik.setFieldValue('bps_expiration_date', date)}
                        />
                      </Card>
                    </Card>
                    <Card width='30vw'>
                      <Input
                        label='Número certificado BPS *'
                        id='client-input-bps_certificate_number'
                        value={basicDataFormik.values.bps_certificate_number || ''}
                        onlyNumbers
                        maxLength={50}
                        placeholder='Número certificado BPS'
                        onChange={(e) =>
                          basicDataFormik.setFieldValue('bps_certificate_number', e.target.value)
                        }
                        disabled={!isApplicationEditable()}
                      />
                      <Span variant='danger' type='2'>
                        {basicDataFormik.errors.bps_certificate_number}
                      </Span>
                    </Card>
                    <Card width='30vw'>
                      <P withMargin={false} variant='neutral-1' type='4'>
                        Fecha de emisión
                      </P>
                      <Card margin={{ top: '3', right: '3' }}>
                        <DatePicker
                          id='client-bps-issue-date'
                          disabled={!isApplicationEditable()}
                          defaultValue={normalizeToIsoDate(basicDataFormik.values.bps_issue_date ?? undefined) || ''}
                          placeholder='Seleccionar fecha'
                          displayFormat='DD/MM/AA'
                          onChange={(date: string) => basicDataFormik.setFieldValue('bps_issue_date', date)}
                        />
                      </Card>
                    </Card>
                  </Card>
                </>
              )}

              <Card display='grid' gap='8px' margin={{ bottom: '4', top: '4' }}>
                <P
                  type='1'
                  variant='neutral-1'
                  withMargin={false}
                  weight='bold'
                  modifier='strong'
                >
                  Datos de contacto
                </P>
              </Card>

              <Card
                display='grid'
                gap='24px'
                alignItems='flex-start'
                style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}
              >
                <Card width='100%'>
                  <Input
                    label='Número de contacto'
                    id='client-input-phone'
                    placeholder='Completar número de contacto'
                    value={basicDataFormik.values.phone_number}
                    maxLength={15}
                    onChange={(e) =>
                      basicDataFormik.setFieldValue(
                        'phone_number',
                        e.target.value.replace(/\D/g, '')
                      )
                    }
                    disabled={!isApplicationEditable()}
                  />
                </Card>

                <Card width='100%'>
                  <Input
                    label='Correo electrónico *'
                    id='client-input-email'
                    placeholder='Completar email'
                    value={basicDataFormik.values.email}
                    maxLength={64}
                    onChange={(e) =>
                      basicDataFormik.setFieldValue('email', e.target.value)
                    }
                    disabled={!isApplicationEditable()}
                  />
                  <Span variant='danger' type='2'>
                    {basicDataFormik.errors.email}
                  </Span>
                </Card>
              </Card>
            </Card>
          </form>
          <form onSubmit={societaryFormik.handleSubmit}>
            <P type='1' variant='neutral-1' weight='bold'>
              Composición societaria
            </P>
            <Card
              variant='info'
              modifier='inverted'
              spacing={'3'}
              width='94%'
              margin={{ bottom: '3' }}
            >
              <Card
                display='flex'
                gap='8px'
                alignItems='center'
                margin={{ bottom: '2' }}
              >
                <Icon name='info_circle_outline' variant='info' />
                <P withMargin={false} variant='neutral-1' weight='bold'>
                  Participación
                </P>
              </Card>
              <P type='1' variant='neutral' withMargin={false}>
                Verificá y completá los datos correspondientes recordando que el
                total de participación debe ser igual al 100%{' '}
              </P>
            </Card>
            {renderComposicionSocietaria()}
            <Card
              display='flex'
              justifyContent='space-between'
              margin={{ top: '3' }}
            >
              <Button
                variant='tertiary'
                size='md'
                onClick={addCorporateStructure}
                disabled={!isApplicationEditable()}
              >
                + Agregar socio
              </Button>
              <Checkbox
                checked={clientInfo.has_valid_corporate_composition}
                onChange={() =>
                  (clientInfo.has_valid_corporate_composition =
                    !clientInfo.has_valid_corporate_composition)
                }
                id='basic_data_has_valid_corporate_composition-checkbox'
                disabled={!isApplicationEditable()}
                size='md'
              >
                La información se ajusta a los documentos
              </Checkbox>
            </Card>
            <Card display='flex' justifyContent='flex-end'>
              <Card
                display='flex'
                justifyContent='flex-end'
                gap='16px'
                margin={{ top: '4' }}
                width='420px'
              >
                <Button
                  id='client-button-save'
                  size='lg'
                  variant='secondary'
                  disabled={
                    !basicDataFormik.isValid ||
                    !societaryFormik.isValid ||
                    isRejected ||
                    isApproved ||
                    isFinished ||
                    isSaving ||
                    pendingAssetUploadsCount > 0
                  }
                  onClick={() => handleSave(false)}
                  fullWidth
                >
                  {isSaving ? (
                    <Spinner size='sm' variant='primary' />
                  ) : (
                    'Guardar'
                  )}
                </Button>
                <Button
                  id='client-button-continue'
                  size='lg'
                  variant='primary'
                  disabled={
                    !basicDataFormik.isValid ||
                    !societaryFormik.isValid ||
                    isSaving ||
                    pendingAssetUploadsCount > 0
                  }
                  onClick={() => handleSave(true)}
                  fullWidth
                >
                  {isSaving ? (
                    <Spinner size='sm' variant='primary' />
                  ) : (
                    'Continuar'
                  )}
                </Button>
              </Card>
            </Card>
          </form>
        </Card>
      ) : (
        <Card
          display='grid'
          justifyContent='center'
          margin={{ top: '11', bottom: '11' }}
        >
          <Card margin='auto'>
            <Image name='user_empty' />
          </Card>
          <P variant='primary' align='center' weight='bold'>
            No hay datos para mostrar
          </P>
          <P variant='primary' align='center' withMargin={false}>
            {isUruguayEnabled
              ? documentSearchType === 'RUT'
                ? 'Buscá un RUT para comenzar.'
                : 'Buscá una Cédula para comenzar.'
              : 'Buscá un CUIT para comenzar.'}
          </P>
        </Card>
      )}
    </Card>
  );
};

export default BasicData;